"use client";

import type { HTMLAttributes, FC, Ref } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import {
  generalTableCellStyle,
  tableCellStyle,
  tableHeaderStyle,
  tableStyle,
} from "./Table.css";
import type { ClassNameProp } from "../../props";
import { typography } from "../../theme/css/typography.css";
import { Text } from "../Text/Text";
import type { TableDataWithStyling } from "../../types";
import { ValueParser } from "../ValueParser/ValueParser";

export type TableProps = HTMLAttributes<HTMLDivElement> &
  ClassNameProp &
  RecipeVariants<typeof tableStyle> & {
    data: TableDataWithStyling;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLTableElement>;
  };

/**
 * A table component.
 */
export const Table: FC<TableProps> = ({ className, data, ref, ...props }) => {
  const headings = data?.headings ?? [];
  const rows = data?.rows ?? [];

  return (
    <table
      ref={ref}
      className={mergeClasses(tableStyle({}), className, typography.bodyM)}
      {...props}
    >
      <thead>
        <tr>
          {headings?.map((heading, i) => (
            <th
              key={i}
              className={mergeClasses(
                generalTableCellStyle({ align: heading.align }),
                tableHeaderStyle,
              )}
            >
              <Text as="span" variant="surfaceSubtitle" color="currentColor50">
                {heading.name}
              </Text>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowI) => (
          <tr key={rowI}>
            {row?.values?.map((cell, colI) => (
              <td
                key={colI}
                className={mergeClasses(
                  generalTableCellStyle({
                    align: headings?.[colI]?.align ?? undefined,
                    bold: cell?.bold,
                  }),
                  tableCellStyle({ lastRow: rowI === rows.length - 1 }),
                  cell.typographyVariant
                    ? typography[cell.typographyVariant]
                    : undefined,
                )}
              >
                <ValueParser value={cell?.value} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
